import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Table } from '@/components/ui/tables/Table';
import type { ExpandableConfig } from '@/components/ui/tables/Table';
import { ExportDropdown, FilterDropdown } from '@/components/ui/filters';
import { Selector } from '@/components/ui/inputs/selector';
import type { AssignedLocationSummaryItem } from '@/api/domains/checkInLocation';
import { useAssignedTab } from '@/hooks/checkInLocation';
import { AssignedTabExpandedRow } from './AssignedTabExpandedRow';


export const AssignedTab: React.FC = () => {
  const { t: translate } = useTranslation('checkInLocation');
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    onSearch,
    onSortChange,
    isLoading,
    onDownload,
    isExporting,
    locationOptions,
    selectedLocationValue,
    onLocationChange,
    employeeOptions,
    selectedEmployeeValue,
    onEmployeeChange,
    onEmployeeSearch,
    loadMoreEmployees,
    employeeHasMore,
    isLoadingEmployees,
  } = useAssignedTab();

  const expandable: ExpandableConfig<AssignedLocationSummaryItem> = useMemo(() => ({
    renderExpanded: (record) => <AssignedTabExpandedRow employees={record.employees} />,
    rowExpandable: (record) => record.assignedCount > 0,
  }), []);

  const filterContent = useMemo(() => (
    <div className="flex flex-col gap-4 min-w-[260px]">
      <Selector
        label={translate('assigned.filters.location')}
        options={locationOptions}
        value={selectedLocationValue}
        onChange={onLocationChange}
        data-testid="assigned-location-filter"
      />
      <Selector
        label={translate('assigned.filters.employee')}
        placeholder={translate('assigned.filters.employeePlaceholder')}
        options={employeeOptions}
        value={selectedEmployeeValue}
        onChange={onEmployeeChange}
        onSearchChange={selectedLocationValue ? undefined : onEmployeeSearch}
        onLoadMore={loadMoreEmployees}
        hasNextPage={employeeHasMore}
        isLoading={isLoadingEmployees}
        data-testid="assigned-employee-filter"
      />
    </div>
  ), [
    translate,
    locationOptions, selectedLocationValue, onLocationChange,
    employeeOptions, selectedEmployeeValue, onEmployeeChange,
    onEmployeeSearch, loadMoreEmployees, employeeHasMore, isLoadingEmployees,
  ]);

  return (
    <div data-testid="assigned-tab">
      <div className="flex items-center justify-between mb-4">
        <FilterDropdown renderExtraFilters={() => filterContent} />
        <ExportDropdown
          onExport={onDownload}
          isLoading={isExporting}
          availableFormats={['EXCEL']}
        />
      </div>
      <Table
        columns={columns}
        data={rows}
        totalRows={total}
        isLoading={isLoading}
        searchable
        searchPlaceholder={translate('assigned.searchPlaceholder')}
        onSearch={onSearch}
        serverSideSort
        onSortChange={onSortChange}
        pagination={{
          page,
          pageSize,
          total,
          onChange: setPage,
          onPageSizeChange: setPageSize,
        }}
        expandable={expandable}
        emptyMessage={translate('assigned.empty')}
        data-testid="assigned-tab-table"
      />
    </div>
  );
};

export default AssignedTab;
